import type { TunnelTestingOverridesService } from '@post-office/message-template-runtime-foundation/types';
import type { UserDetailsProvider } from '@post-office/service-identity';
import type { PipelineMessage } from '@post-office/shared-contracts/types';
import type {
	TunnelManagerFactory,
	TunnelRequestManager,
} from '@post-office/tunnel-request-manager/types';

import type { RequestContext } from '../../../types';

const TUNNEL_FEATURE_GATE = 'post-office_enable-in-app-v2-placement-tunnel';

export interface TunnelDependencies {
	tunnelTestingOverridesService: TunnelTestingOverridesService;
	userDetailsProvider: UserDetailsProvider;
	tunnelManagerFactory?: TunnelManagerFactory;
	whitelistProxyHost: string;
	allowRemoteExecution: boolean;
}

export type TunnelDecision =
	| {
			enabled: true;
			messageTemplateIds: string[];
			remoteTunnelManager: TunnelRequestManager;
	  }
	| {
			enabled: false;
	  };

export const checkShouldTunnelStage = async ({
	request,
	pipelineMessages,
	tunnelDependencies,
	stageName,
	placementId,
}: {
	request: RequestContext;
	pipelineMessages: PipelineMessage[];
	tunnelDependencies: TunnelDependencies;
	stageName: string;
	placementId: string;
}): Promise<TunnelDecision> => {
	const recipientAccountId = request.orchestrationContext.recipient.userId;

	if (!isRemoteExecutionAllowed(tunnelDependencies)) {
		return { enabled: false };
	}

	if (!isTunnelFeatureGateEnabled(request, recipientAccountId)) {
		return { enabled: false };
	}

	try {
		const { hasTunnelledMessageTemplateIds, messageTemplateIds } =
			await hasTunnelledMessageTemplateIdsInPipeline(
				tunnelDependencies,
				recipientAccountId,
				pipelineMessages,
			);
		if (!hasTunnelledMessageTemplateIds) {
			return { enabled: false };
		}

		const remoteTunnelManager = await setUpRemoteTunnelManager(
			tunnelDependencies,
			recipientAccountId,
			request,
			placementId,
			stageName,
		);

		if (!remoteTunnelManager) {
			return { enabled: false };
		}

		return {
			enabled: true,
			messageTemplateIds,
			remoteTunnelManager,
		};
	} catch (error) {
		request.logger.warn(
			{ error, recipientAccountId, placementId, stageName },
			'[Tunnel] Failed to check tunnel overrides or create tunnel manager',
		);
		return { enabled: false };
	}
};

// This is the environment variable set for all tunnelling cases, regardless of what delivery channel is used.
// Tunnelling is only supported in Staging commercial, where this variable is set to true.
const isRemoteExecutionAllowed = (tunnelDependencies: TunnelDependencies): boolean => {
	return tunnelDependencies.allowRemoteExecution === true;
};

const isTunnelFeatureGateEnabled = (
	request: RequestContext,
	recipientAccountId: string,
): boolean => {
	return request.statsig.checkGate(
		{
			identifiers: {
				atlassianAccountId: recipientAccountId,
			},
		},
		TUNNEL_FEATURE_GATE,
	);
};

const getTunnelOverrides = async (
	tunnelDependencies: TunnelDependencies,
	recipientAccountId: string,
): Promise<string[] | null> => {
	const overrides =
		await tunnelDependencies.tunnelTestingOverridesService.getOverridesByAccountId(
			recipientAccountId,
		);

	if (!overrides?.messageTemplateIds?.length) {
		return null;
	}

	return overrides.messageTemplateIds;
};

const hasTunnelledMessageTemplateIdsInPipeline = async (
	tunnelDependencies: TunnelDependencies,
	recipientAccountId: string,
	pipelineMessages: PipelineMessage[],
): Promise<{
	hasTunnelledMessageTemplateIds: boolean;
	messageTemplateIds: string[];
}> => {
	const overrides = await getTunnelOverrides(tunnelDependencies, recipientAccountId);
	if (!overrides) {
		return { hasTunnelledMessageTemplateIds: false, messageTemplateIds: [] };
	}

	const allMessageTemplateIdsInPipeline = new Set(
		pipelineMessages.map((msg) => msg.messageTemplateId),
	);
	const tunnelledMessageTemplateIds = overrides.filter((id) =>
		allMessageTemplateIdsInPipeline.has(id),
	);

	return {
		hasTunnelledMessageTemplateIds: tunnelledMessageTemplateIds.length > 0,
		messageTemplateIds: tunnelledMessageTemplateIds,
	};
};

const setUpRemoteTunnelManager = async (
	tunnelDependencies: TunnelDependencies,
	recipientAccountId: string,
	request: RequestContext,
	placementId: string,
	stageName: string,
): Promise<TunnelRequestManager | null> => {
	// Check if factory is available (only provided in Node.js environments)
	if (!tunnelDependencies.tunnelManagerFactory) {
		request.logger.warn(
			{ placementId, stageName },
			'[Tunnel] No tunnel manager factory provided - tunneling unavailable',
		);
		return null;
	}

	const userDetails = await tunnelDependencies.userDetailsProvider.getIdentityUserDetails(
		recipientAccountId,
		true,
	);
	const recipientEmail = userDetails.email;
	const isAtlassianStaff =
		recipientEmail.endsWith('@atlassian.com') || recipientEmail.endsWith('@usersinbuckets.com');

	if (!isAtlassianStaff) {
		request.logger.warn(
			{ recipientEmail, placementId, stageName },
			'[Tunnel] Recipient is not Atlassian staff, not setting up remote tunnel manager',
		);
		return null;
	}

	const staffId = recipientEmail.split('@')[0] || 'unknown';

	// Use the injected factory to create the tunnel manager
	const remoteTunnelManager = tunnelDependencies.tunnelManagerFactory.createTunnelManager(
		staffId,
		request.logger,
		request.metrics,
		tunnelDependencies.whitelistProxyHost,
	);

	return remoteTunnelManager;
};
